// Norwegian (Bokmål) dental vocabulary, FDI numbering. All forms are folded
// (see fold.ts): å → a, ø → o, æ → ae, so "øvre høyre sekser" matches
// "ovre hoyre sekser". Teeth: digits ("16"), words ("seksten", "tjueseks",
// "seksogtjue"), FDI pairs ("en-seks", "fire sju"), position nouns
// ("sekseren", "øvre høyre sekser") and tooth names ("første molar").

import { isValidTooth, type Surface } from '@/utils/dental';
import type {
  CodeRule,
  Lexicon,
  LocationWord,
  MaterialRule,
  NumberRead,
  PhraseHit,
  Token,
} from './types';

const UNITS: Record<string, number> = {
  en: 1,
  ett: 1,
  to: 2,
  tre: 3,
  fire: 4,
  fem: 5,
  seks: 6,
  sju: 7,
  syv: 7,
  atte: 8,
  ni: 9,
};

const TEENS: Record<string, number> = {
  elleve: 11,
  tolv: 12,
  tretten: 13,
  fjorten: 14,
  femten: 15,
  seksten: 16,
  sytten: 17,
  atten: 18,
  nitten: 19,
};

const TENS: [string, number][] = [
  ['tredve', 30],
  ['tretti', 30],
  ['seksti', 60],
  ['sytti', 70],
  ['tjue', 20],
  ['tyve', 20],
  ['forti', 40],
  ['femti', 50],
  ['atti', 80],
];

/** "sekser", "sekseren", "sjueren": a position without a quadrant. */
const POSITION_NOUNS: [string, number][] = [
  ['sekser', 6],
  ['sjuer', 7],
  ['syver', 7],
  ['femmer', 5],
  ['firer', 4],
  ['treer', 3],
  ['toer', 2],
  ['ener', 1],
  ['atter', 8],
  ['visdomstann', 8],
  ['hjornetann', 3],
];

function read(value: number, used: number, adjectival = false): NumberRead {
  return { value, used, adjectival, from: false, to: false };
}

function readTens(tokens: Token[], i: number, base: number, rest: string): NumberRead | null {
  if (rest) {
    const unit = UNITS[rest.replace(/^-/, '')];
    return unit ? read(base + unit, 1) : null;
  }
  const next = tokens[i + 1]?.norm;
  const unit = next ? UNITS[next] : undefined;
  return unit ? read(base + unit, 2) : null;
}

/** "seksogtjue" (older form: unit, "og", tens). */
function readUnitFirst(norm: string): NumberRead | null {
  const m = /^([a-z]+)og([a-z]+)$/.exec(norm);
  if (!m) return null;
  const unit = UNITS[m[1]];
  const tens = TENS.find(([word]) => word === m[2]);
  return unit && tens ? read(tens[1] + unit, 1) : null;
}

function readNumber(tokens: Token[], i: number): NumberRead | null {
  const norm = tokens[i].norm;
  const digits = /^(\d{2})(-?[a-z]{1,3})?$/.exec(norm);
  if (digits) return read(Number(digits[1]), 1, !!digits[2]);
  if (TEENS[norm]) return read(TEENS[norm], 1);
  const unitFirst = readUnitFirst(norm);
  if (unitFirst) return unitFirst;
  const tens = TENS.find(([word]) => norm.startsWith(word));
  return tens ? readTens(tokens, i, tens[1], norm.slice(tens[0].length)) : null;
}

/** "en-seks" → 16, "fire sju" → 47. */
function readQuadrantPair(a: string, b: string): number | null {
  const q = UNITS[a];
  const p = UNITS[b];
  if (!(q && p) || q > 8 || p > 8) return null;
  const value = q * 10 + p;
  return isValidTooth(value) ? value : null;
}

function readPosition(norm: string): number | null {
  return POSITION_NOUNS.find(([stem]) => norm.startsWith(stem))?.[1] ?? null;
}

const NAMED: [string[], number][] = [
  [['sentral', 'fortann'], 1],
  [['lateral', 'fortann'], 2],
  [['forste', 'premolar'], 4],
  [['andre', 'premolar'], 5],
  [['forste', 'molar'], 6],
  [['andre', 'molar'], 7],
  [['tredje', 'molar'], 8],
];

function readPositionAt(tokens: Token[], i: number, afterLocation: boolean) {
  const n = tokens[i]?.norm;
  if (!n) return null;
  for (const [words, position] of NAMED) {
    if (words.every((w, k) => tokens[i + k]?.norm.startsWith(w))) {
      return { position, used: words.length };
    }
  }
  const noun = readPosition(n);
  if (noun !== null) return { position: noun, used: 1 };
  // A bare "seks" is a count; after "øvre høyre" it is the position.
  const numeral = UNITS[n] ?? (/^[1-8]$/.test(n) ? Number(n) : undefined);
  return afterLocation && numeral && numeral <= 8 ? { position: numeral, used: 1 } : null;
}

const LOCATIONS: [RegExp, LocationWord][] = [
  // Not bare "over"/"under": they are everyday prepositions ("under en krone").
  [/^(ovre|oppe|overkjeve\w*)$/, 'upper'],
  [/^(nedre|nede|underkjeve\w*)$/, 'lower'],
  [/^hoyre$/, 'right'],
  [/^venstre$/, 'left'],
  [/^melke\w*$/, 'primary'],
  [/^(side|siden|i|pa)$/, 'filler'],
];

function location(norm: string): LocationWord | null {
  return LOCATIONS.find(([re]) => re.test(norm))?.[1] ?? null;
}

const CODE_RULES: CodeRule[] = [
  { code: 'root_canal', stems: ['rotfyll', 'rotfylt', 'rotbehand', 'endodont', 'endo'] },
  { code: 'periapical', stems: ['periapikal', 'apikal', 'granulom', 'absess', 'abscess'] },
  { code: 'root_remnant', stems: ['rotrest', 'radix'] },
  // "devital", "nekrotisk", "død tann"
  { code: 'devital', stems: ['devital', 'avital', 'nekro'], exact: ['dod', 'dode'] },
  { code: 'caries', stems: ['karies', 'karios'], exact: ['hull', 'rate', 'raten'] },
  { code: 'sealant', stems: ['fissurforsegl', 'forsegl'] },
  { code: 'inlay', stems: ['innlegg', 'inlay', 'onlay'] },
  { code: 'filling', stems: ['fylling', 'fylt', 'restaur'], exact: ['fylles'] },
  { code: 'fracture', stems: ['fraktur', 'sprekk', 'brukket', 'knekt', 'avbrekk'] },
  { code: 'bridge_pontic', stems: ['mellomledd', 'pontic'] },
  { code: 'bridge_abutment', stems: ['bropilar', 'brostotte', 'pilar'] },
  { code: 'bridge_abutment', exact: ['bro', 'broen', 'broa'], genericBridge: true },
  { code: 'crown', stems: ['krone'] },
  { code: 'veneer', stems: ['fasett', 'veneer'], exact: ['skall'] },
  { code: 'extraction', stems: ['ekstrah', 'ekstraksjon', 'trekk', 'trukk'] },
  { code: 'missing', stems: ['mangl'], exact: ['mangler', 'borte', 'tapt', 'tapte', 'mistet'] },
  { code: 'implant', stems: ['implantat'] },
  { code: 'mobility', stems: ['mobil', 'vakl'], exact: ['los', 'lose', 'lost'] },
  { code: 'calculus', stems: ['tannstein'], exact: ['stein'] },
  { code: 'impacted', stems: ['retinert', 'impakt'] },
  { code: 'wear', stems: ['slitasje', 'slitt', 'erosjon', 'abrasjon', 'attrisjon'] },
  { code: 'sound', exact: ['frisk', 'intakt', 'ua', 'u.a', 'ia', 'i.a'] },
];

const SURFACE_STEMS: [string, Surface][] = [
  ['mesi', 'M'],
  ['dist', 'D'],
  ['okklus', 'O'],
  // "incisal", not "incisiver" (incisors)
  ['incisa', 'I'],
  ['insisa', 'I'],
  ['bukk', 'B'],
  ['labi', 'B'],
  ['vestib', 'B'],
  ['lingu', 'L'],
  ['palat', 'L'],
  ['oral', 'L'],
];

const MATERIAL_RULES: MaterialRule[] = [
  { material: 'metal_ceramic', stems: ['metallkeram', 'metall-keram'] },
  { material: 'lithium_disilicate', stems: ['emax', 'e-max', 'litium', 'disilikat'] },
  { material: 'zirconia', stems: ['zirkon'] },
  { material: 'glass_ionomer', stems: ['glassionomer', 'ionomer', 'gic'] },
  { material: 'composite', stems: ['komposit'] },
  { material: 'amalgam', stems: ['amalgam'] },
  { material: 'ceramic', stems: ['keram', 'porselen'] },
  { material: 'gold', stems: ['gull'] },
  { material: 'titanium', stems: ['titan'] },
  { material: 'temporary', stems: ['midlertid', 'provisor'] },
];

function readPhrase(tokens: Token[], i: number): PhraseHit | null {
  const n = tokens[i].norm;
  const next = tokens[i + 1]?.norm ?? '';
  // "gjenværende rot", "ikke vital"
  if (n.startsWith('gjenvaer') && /^(rot|roten|rotter|rotene)$/.test(next)) {
    return { kind: 'code', code: 'root_remnant', used: 2 };
  }
  if (n === 'ikke' && next.startsWith('vital')) return { kind: 'code', code: 'devital', used: 2 };
  // "falt ut", "falte ut", "slått ut" (knocked out)
  if (/^(falt|falte|faller|slatt|slo)$/.test(n) && next === 'ut') {
    return { kind: 'code', code: 'missing', used: 2 };
  }
  if (n === 'metall' && next.startsWith('keram')) {
    return { kind: 'material', material: 'metal_ceramic', used: 2 };
  }
  return null;
}

const NOTES: [string, string][] = [
  ['sekund', 'sekundær'],
  ['dyp', 'dyp'],
  ['begynnende', 'begynnende'],
  ['gammel', 'gammel'],
  ['defekt', 'defekt'],
  ['lekk', 'lekkasje'],
];

const GRADES: Record<string, string> = {
  en: 'I',
  '1': 'I',
  i: 'I',
  forste: 'I',
  to: 'II',
  '2': 'II',
  ii: 'II',
  andre: 'II',
  tre: 'III',
  '3': 'III',
  iii: 'III',
  tredje: 'III',
};

/** "grad to", "grad II", "andre grad". */
function readGrade(tokens: Token[], i: number): { grade: string; used: number } | null {
  const n = tokens[i].norm;
  const next = tokens[i + 1]?.norm ?? '';
  if (n === 'grad' && GRADES[next]) return { grade: GRADES[next], used: 2 };
  if (GRADES[n] && next.startsWith('grad')) return { grade: GRADES[n], used: 2 };
  return null;
}

/**
 * Material + procedure written as one word: "amalgamfylling" → amalgam,
 * fylling; "zirkoniakrone" → zirkonia, krone. Only when the first part is a
 * material, so "rotfylling" (root canal treatment) stays whole.
 */
function splitCompound(norm: string): string[] | null {
  const m =
    /^(.+?)-?(fyllingene|fyllinger|fylling|fylt|kronene|kroner|kronen|krone|innlegg|fasetter|fasett)$/.exec(
      norm
    );
  if (!m) return null;
  const material = MATERIAL_RULES.some((r) => r.stems.some((st) => m[1].startsWith(st)));
  return material ? [m[1], m[2]] : null;
}

const FILLER_WORDS = new Set([
  'tann',
  'tannen',
  'tenner',
  'tennene',
  'nr',
  'nummer',
  'pa',
  'i',
  'er',
  'har',
  'med',
  'og',
  'en',
  'et',
  'ei',
  'flate',
  'flaten',
  'flater',
  'side',
  'siden',
  'ved',
  'ogsa',
  'som',
  'det',
  'den',
  'sees',
  'ses',
  'synlig',
  'regio',
  'region',
]);

export const nb: Lexicon = {
  lang: 'nb',
  readNumber,
  readQuadrantPair,
  readPosition,
  readPositionAt,
  location,
  rangeFrom: new Set(['fra']),
  rangeTo: new Set(['til']),
  readPhrase,
  splitCompound,
  codeRules: CODE_RULES,
  surfaceStems: SURFACE_STEMS,
  lowercaseSurfaceAbbr: new Set(['mod', 'mo', 'modb', 'modl']),
  // A sentence starting with "I" ("in") is not "incisal".
  excludedSurfaceAbbr: new Set(['I']),
  materialRules: MATERIAL_RULES,
  isPlanned: (n) =>
    ['planlagt', 'planlegg', 'trenger', 'behov', 'indisert'].some((st) => n.startsWith(st)) ||
    // exact: "skall" is a veneer
    n === 'skal' ||
    // "skal trekkes": the passive "-es" of a procedure verb
    /^(trekkes|fylles|rotfylles|krones)$/.test(n),
  note: (n) => NOTES.find(([stem]) => n.startsWith(stem))?.[1] ?? null,
  negationWords: new Set(['ikke', 'ingen', 'uten', 'nei']),
  correctionWords: new Set(['men']),
  fillerWords: FILLER_WORDS,
  separatorWords: new Set(['deretter', 'neste', 'videre']),
  commandPatterns: [
    {
      command: 'approve_all',
      pattern: /^(godkjenn(er)?( alle| alt)?|alle godkjent)$/,
    },
    { command: 'undo', pattern: /^(angre( det)?|slett (den )?siste|fjern (den )?siste|avbryt)$/ },
    { command: 'yes', pattern: /^ja$/ },
    { command: 'no', pattern: /^nei$/ },
  ],
  noChangePattern:
    /(ingen endring|uendret|resten (er )?(uendret|som for|ok|i orden)|alt annet (er )?(uendret|ok|i orden))/,
  price: { unit: 'dinar(?:er)?|rsd', thousand: 'tusen' },
  readGrade,
};
